import { CoreUILib } from './contexts/core-ui-lib.js';
import { APIContext } from './contexts/api-context.js';
import DisplayCreative from './display-creative.js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useTopicTemplate from './hooks/use-topic-template.js';
import { useSelector } from './hooks/use-selector.js';
import { scopeLogger } from './lib/scope-logger.js';
import { useExclusiveView } from './hooks/use-exclusive-view.js';
import { ErrorBoundary } from './components/error-boundary.js';
import { ET } from './lib/ET/ET.js';
import { useEventTracker } from './hooks/use-event-tracker.js';
import { useAssetsPreload } from './hooks/use-assets-preload.js';

export default PromoFactory;

const currentDeviceIdSelector = state => state.user?.device_id;

/**
 * @param {ExtensionDescription} description
 * @param {ExtensionDependencies} dependencies
 * @return {{UI: (function({UI: *}))}}
 * @constructor
 */
function PromoFactory (description, dependencies) {
	const {
		mns,
		API,
	} = dependencies;

	const console = scopeLogger('RMI:Promo');
	const eventTracker = new ET(API.hostApp.trackEvent);

	const PromoLandingExtension = function (props) {

		const {
			UI,
			currentObject: currentVideo,
			options
		} = props;
		eventTracker.enabled = options.trackEvents;

		const deviceId = useSelector(currentDeviceIdSelector, undefined);

		/**
		 * @type {React.MutableRefObject<HTMLElement>}
		 */
		const ref = useRef();
		const setExclusiveView = useExclusiveView(ref);

		const [ creative, setCreative ] = useState(null);
		const assetsPreload = useAssetsPreload(creative?.assets);

		useEffect(() => {
			if (assetsPreload.error) {
				creative?.dispatch?.error(assetsPreload.error);
				closeCreative('assets-preload-error');
			}
		}, [ assetsPreload, creative ]);

		const onError = useCallback((error) => {
			creative?.dispatch?.error(error);
			closeCreative(`Error display: ${error}`);
		}, [creative]);

		/** Subscribe on "personal" topic */
		const topicContext = useMemo(() => {
			const objectId = currentVideo?.object_id;
			if (objectId === undefined || deviceId === undefined) {
				return;
			}

			return {
				video_id: objectId,
				device_id: deviceId,
			};
		}, [ currentVideo, deviceId ]);
		const [ eventsTopic ] = useTopicTemplate(options.subscribe, topicContext);

		useEffect(() => {
			eventTracker.video = currentVideo;
		}, [currentVideo]);

		const onMessage = useCallback((message) => {
			const {
				payload: {
					cmd,
					type,
					data
				},
				topic,
			} = message;

			if (topic !== eventsTopic) {
				return;
			}

			if (type !== "promo") {
				return;
			}

			if(cmd === "display") {
				// addTrackingEvents(data.creative);
				eventTracker.creative = data.creative;
				setCreative(data.creative);
			}

			if (cmd === 'close'){
				if (data.diff < 0) {
					closeCreative('jump to early MUST close');
				} else {
					let message = 'time slot ended, ';
					if (slideRef.current.autoClose !== null){
						message += 'ignore "close" command, rely on autoClose';
						console.log(message);
					} else {
						message += 'CLOSE creative dur to autoClose is not defined';
						closeCreative(message);
					}
				}
			}

			if (cmd === 'cancel') {
				console.log('cancel display creative');
				onError(data.reason);
			}

		}, [ eventsTopic, onError ]);

		const slideRef = useRef(null);
		useEffect(() => {
			const target = ref.current;
			const listener = (ev) => {
				slideRef.current = ev.detail.origin.target;
			};

			target.addEventListener('slide-view', listener);

			return () => {
				target.removeEventListener('slide-view', listener);
			};
		}, []);

		/* subscribe on all MNS messages */
		useEffect(() => {
			const subscription = mns.message$.subscribe(onMessage);

			return () => {
				subscription.unsubscribe();
			};
		}, [onMessage]);

		/* notify root to create topic subscription */
		useEffect(() => {
			if (eventsTopic === undefined) {
				console.warn('topic is not defined');
				return;
			}

			console.log(`subscribe to topic: ${eventsTopic}`);

			return API.mns.subscribe(eventsTopic);
		}, [ eventsTopic ]);

		const externalAction = useCallback((action) => {
			API.hostApp.sendEvent("navigation", {
				target: action.link,
				referer: `interactive-promo://${creative.id}`
			});
			closeCreative('request external action');
		}, [ creative ]);

		const closeCreative = useCallback((reason) => {
			console.debug('Close creative', reason);
			setCreative(null);
		}, [creative]);

		useEventTracker(eventTracker, ref);

		/** rest actions, to be done */

		const fullscreenEffect = useCallback((isFullscreen) => {
			document.body.classList.toggle('is-fullscreen', isFullscreen);
			if (false === isFullscreen) {
				// TODO зачем это, надо ли?
				// API.hostApp.releaseForeground();
			}
		}, []);
		useFullscreen(fullscreenEffect);

		return (
			<React.StrictMode>
				<section data-role="interactive-extension" data-name="promo" ref={ref}>
					<CoreUILib.Provider value={UI}>
						<APIContext.Provider value={API}>
							{assetsPreload.ok ? (
								<ErrorBoundary onFatalError={onError} key={creative?.id}>
									<DisplayCreative
										creative={creative}
										onRequestExclusive={setExclusiveView}
										onRequestClose={closeCreative}
										onRequestAction={externalAction}
									/>
								</ErrorBoundary>
							) : null
							}
						</APIContext.Provider>
					</CoreUILib.Provider>
				</section>
			</React.StrictMode>
		);
	};

	return {
		UI: PromoLandingExtension,
		test : (schema) => {
			//Validate by type/version/etc...
			return schema?.promoCreativeType === "promo";
		},
	};
}

function useFullscreen (cb) {
	useEffect(() => {
		const onResize = (ev) => {
			const frame = ev.currentTarget;
			const minRatio = Math.min(frame.innerWidth / screen.width, frame.innerHeight / screen.height);
			const isFullscreen = minRatio >= 0.9;
			// Ok, ok, it's not the "real" fullscreen, just frame large enough to pretend to be "fullscreen"
			cb(isFullscreen);
		};

		window.addEventListener('resize', onResize);
		onResize({
			currentTarget: window
		});

		return () => {
			window.removeEventListener('resize', onResize);
		};
	}, [ cb ]);
}
